import React from 'react';

/**
 * Cockpit Badge — status pill. Mono uppercase, soft tinted field,
 * optional pulse dot. Tones map to status tokens.
 */
export type BadgeTone = 'up' | 'warn' | 'down' | 'info' | 'idle' | 'accent' | 'neutral';

export interface BadgeProps {
  tone?: BadgeTone;
  dot?: boolean;
  pulse?: boolean;
  count?: string | number;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  /** Native tooltip. */
  title?: string;
}

const tones: Record<BadgeTone, { color: string; bg: string }> = {
  up: { color: 'var(--status-up)', bg: 'var(--status-up-soft)' },
  warn: { color: 'var(--status-warn)', bg: 'var(--status-warn-soft)' },
  down: { color: 'var(--status-down)', bg: 'var(--status-down-soft)' },
  info: { color: 'var(--status-info)', bg: 'var(--status-info-soft)' },
  idle: { color: 'var(--status-idle)', bg: 'var(--status-idle-soft)' },
  accent: { color: 'var(--accent-text)', bg: 'var(--accent-soft)' },
  neutral: { color: 'var(--text-muted)', bg: 'var(--surface-raised)' },
};

export default function Badge({ tone = 'neutral', dot = false, pulse = false, count, children, style, title }: BadgeProps) {
  const t = tones[tone] || tones.neutral;
  return (
    <span
      className="ck-badge"
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: '20px',
        padding: count !== undefined ? '0 6px' : '0 8px',
        borderRadius: 'var(--radius-pill)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-2xs)',
        fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
        // Without this the line box carries the font's descender space, which
        // uppercase never uses, so the caps sit ~0.6px above centre.
        lineHeight: 1,
        letterSpacing: 'var(--tracking-label)',
        textTransform: 'uppercase',
        color: t.color,
        background: t.bg,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {dot ? (
        <span
          style={{
            width: '6px',
            height: '6px',
            borderRadius: 'var(--radius-pill)',
            background: 'currentColor',
            animation: pulse ? 'ck-badge-pulse 2s var(--ease-in-out) infinite' : 'none',
          }}
        />
      ) : null}
      {count !== undefined ? count : children}
    </span>
  );
}
